import React, { useEffect, useState, useRef } from 'react';
import { jsPDF } from 'jspdf';
import { host } from './script/variables';
import './style/Invoice.css';

/* ── quotes ──────────────────────────────────────────────── */
const QUOTES = [
  'Good food is the foundation of genuine happiness.',
  "Every meal is a memory — we hope today's was a great one.",
  'Food tastes better when shared. Come back with more friends!',
  'We cook with love, and we hope you tasted every bit of it.',
  'Thank you for choosing us. May every bite have been a delight!',
  'A great meal is an act of generosity — thank you for letting us serve you.',
];
const quote = QUOTES[Math.floor(Math.random() * QUOTES.length)];

/* ── helpers ─────────────────────────────────────────────── */
const fmt   = (n) => `₹${Number(n).toFixed(2)}`;
const fmtC  = (n) => `Rs.${Number(n).toFixed(2)}`;  // for PDF
const safeN = (v) => (!isNaN(Number(v)) ? Number(v) : 0);

const formatDate = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

/* ── Financials shaping ───────────────────────────────────────────
   The API is the ONLY source of tax numbers — they come from the
   TaxCollection snapshot recorded when the order was completed.
   This function just normalizes that payload into the shape the
   render code (screen + PDF) uses. It computes nothing tax-related;
   `combinedTaxRate` is just a display sum of the already-recorded
   per-line rates, not a recalculation of any amount.
──────────────────────────────────────────────────────────────── */
const shapeFinancials = (financials = {}) => {
  const taxBreakdown = (financials.taxBreakdown || []).map((t) => ({
    group:  t.type || t.name,
    label:  t.name,
    rate:   Number(t.rate) || 0,
    amount: Number(t.amount) || 0,
  }));
  const combinedTaxRate = taxBreakdown.reduce((s, t) => s + t.rate, 0);

  return {
    taxableSub:    Number(financials.taxableSub)    || 0,
    nonTaxableSub: Number(financials.nonTaxableSub) || 0,
    taxBreakdown,
    combinedTaxRate,
    totalTax:      Number(financials.totalTax)   || 0,
    grandTotal:    Number(financials.grandTotal) || 0,
    isEstimate:    financials.source === 'fallback-no-snapshot',
  };
};

/* ══════════════════════════════════════════════════════════
   PDF GENERATOR  (pure jsPDF canvas, no autoTable)
══════════════════════════════════════════════════════════ */
const C = {
  dark:      [15,  25,  35],
  red:       [192, 57,  43],
  mid:       [74,  85,  104],
  light:     [248, 249, 251],
  border:    [225, 230, 235],
  white:     [255, 255, 255],
  green:     [16,  185, 129],
  amberBg:   [255, 251, 235],
  amberBdr:  [253, 230, 138],
  rowAlt:    [252, 252, 253],
  headBg:    [230, 232, 236],
};

const pdfFill   = (doc, rgb) => doc.setFillColor(...rgb);
const pdfStroke = (doc, rgb) => doc.setDrawColor(...rgb);
const pdfColor  = (doc, rgb) => doc.setTextColor(...rgb);
const pdfRect   = (doc, x, y, w, h, s = 'F') => doc.rect(x, y, w, h, s);
const pdfRRect  = (doc, x, y, w, h, r = 4, s = 'F') => doc.roundedRect(x, y, w, h, r, r, s);
const pdfTxt    = (doc, str, x, y, opts = {}) => doc.text(String(str ?? '—'), x, y, opts);
const hline     = (doc, x1, x2, y, lw = 0.4, rgb = C.border) => {
  doc.setLineWidth(lw); pdfStroke(doc, rgb);
  doc.line(x1, y, x2, y);
};

const drawTable = (doc, sx, sy, cols, rows, opt = {}) => {
  const {
    rowH = 20, headH = 22, fontSize = 8, headFontSize = 7.5,
    headBgColor = C.dark, headColor = C.white, bodyColor = C.dark,
    borderColor = C.border, altRow = true, cellPadX = 6,
  } = opt;

  const totalW = cols.reduce((s, c) => s + c.width, 0);

  pdfFill(doc, headBgColor);
  pdfRect(doc, sx, sy, totalW, headH);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(headFontSize);
  pdfColor(doc, headColor);

  let cx = sx;
  cols.forEach((col) => {
    const a  = col.align || 'left';
    const tx = a === 'center' ? cx + col.width / 2
             : a === 'right'  ? cx + col.width - cellPadX
             : cx + cellPadX;
    pdfTxt(doc, col.label, tx, sy + headH / 2 + headFontSize / 3, a !== 'left' ? { align: a } : {});
    cx += col.width;
  });

  let y = sy + headH;
  rows.forEach((row, ri) => {
    pdfFill(doc, altRow && ri % 2 === 1 ? C.rowAlt : C.white);
    pdfRect(doc, sx, y, totalW, rowH);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(fontSize);
    pdfColor(doc, bodyColor);

    cx = sx;
    row.forEach((cell, ci) => {
      const col = cols[ci];
      const a   = col?.align || 'left';
      const tx  = a === 'center' ? cx + col.width / 2
                : a === 'right'  ? cx + col.width - cellPadX
                : cx + cellPadX;
      if (col?.isGstCol) {
        pdfColor(doc, cell === 'Yes' ? C.green : C.mid);
        doc.setFont('helvetica', cell === 'Yes' ? 'bold' : 'normal');
      }
      pdfTxt(doc, cell, tx, y + rowH / 2 + fontSize / 3, a !== 'left' ? { align: a } : {});
      if (col?.isGstCol) { pdfColor(doc, bodyColor); doc.setFont('helvetica', 'normal'); }
      cx += col?.width || 0;
    });

    hline(doc, sx, sx + totalW, y + rowH, 0.3, borderColor);
    y += rowH;
  });

  pdfStroke(doc, borderColor);
  doc.setLineWidth(0.5);
  doc.rect(sx, sy, totalW, headH + rows.length * rowH, 'S');
  return y;
};

const buildPDF = (data) => {
  const { invoice, restaurant, customer, order, items } = data;
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W   = doc.internal.pageSize.getWidth();
  const PAD = 40;

  // Tax numbers as recorded for THIS order — never recomputed from
  // live config. See shapeFinancials().
  const f = shapeFinancials(data.financials);

  /* ── 1 Header ── */
  pdfFill(doc, C.dark);
  pdfRRect(doc, PAD, 24, W - PAD * 2, 88, 6);

  pdfColor(doc, C.white);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(20);
  pdfTxt(doc, restaurant.name, PAD + 18, 55);

  if (restaurant.address) {
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    pdfColor(doc, [170, 185, 200]);
    pdfTxt(doc, restaurant.address, PAD + 18, 70, { maxWidth: W - PAD * 2 - 130 });
  }

  pdfFill(doc, C.red);
  pdfRRect(doc, W - PAD - 112, 34, 102, 30, 4);
  pdfColor(doc, C.white);
  doc.setFont('helvetica', 'bold'); doc.setFontSize(13);
  pdfTxt(doc, 'INVOICE', W - PAD - 61, 54, { align: 'center' });

  pdfColor(doc, [160, 180, 200]);
  doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
  pdfTxt(doc, invoice.invoiceNumber,      W - PAD - 61, 70, { align: 'center' });
  pdfTxt(doc, formatDate(invoice.placedAt), W - PAD - 61, 82, { align: 'center' });

  /* ── 2 Customer + Order meta ── */
  let y = 128;
  const pills = [
    { label: 'CUSTOMER',   value: customer.name },
    { label: 'ORDER TYPE', value: order.type },
    { label: 'PAYMENT',    value: order.paymentMethod },
    { label: 'STATUS',     value: order.deliveryStatus },
  ];
  const pillW = (W - PAD * 2 - 12) / 4;
  pills.forEach((p, i) => {
    const px = PAD + i * (pillW + 4);
    pdfFill(doc, C.light); pdfStroke(doc, C.border);
    doc.setLineWidth(0.5);
    pdfRRect(doc, px, y, pillW, 36, 4, 'FD');
    pdfColor(doc, [140, 155, 170]);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5);
    pdfTxt(doc, p.label, px + pillW / 2, y + 12, { align: 'center' });
    pdfColor(doc, C.dark);
    doc.setFont('helvetica', 'bold'); doc.setFontSize(9);
    pdfTxt(doc, p.value, px + pillW / 2, y + 26, { align: 'center' });
  });

  /* ── 3 Items table ── */
  y = 182;
  pdfFill(doc, C.red); pdfRRect(doc, PAD, y, 4, 14, 2);
  pdfColor(doc, C.dark); doc.setFont('helvetica', 'bold'); doc.setFontSize(10);
  pdfTxt(doc, 'Order Items', PAD + 10, y + 10);
  y += 18;

  const tableW = W - PAD * 2;
  const cols = [
    { label: '#',          width: 22,           align: 'center' },
    { label: 'Item',       width: tableW - 278, align: 'left'   },
    { label: 'Size',       width: 40,           align: 'center' },
    { label: 'Taxable',    width: 32,           align: 'center', isGstCol: true },
    { label: 'Qty',        width: 26,           align: 'center' },
    { label: 'Unit Price', width: 58,           align: 'right'  },
    { label: 'Disc.',      width: 36,           align: 'center' },
    { label: 'Amount',     width: 64,           align: 'right'  },
  ];

  y = drawTable(doc, PAD, y, cols, items.map((item, idx) => [
    String(idx + 1),
    item.name,
    item.size,
    item.isTaxable ? 'Yes' : 'No',
    String(item.qty),
    fmtC(item.unitPrice),
    item.discount > 0 ? `${item.discount}%` : '—',
    fmtC(item.lineNet),
  ]), { rowH: 20, headH: 22 });

  /* ── 4 Tax box + Totals ──
     Both blocks are built from f.taxBreakdown, sourced from the
     order's own TaxCollection snapshot, so the box height and row
     count adapt to however many tax lines were recorded for it. */
  y += 16;
  const gstW = 224;
  const totW = W - PAD * 2 - gstW - 14;
  const totX = PAD + gstW + 14;

  const taxHeadH   = 18;
  const taxRowH    = 17;
  const taxRowCnt  = Math.max(f.taxBreakdown.length, 1) + 1; // +1 for the Total row
  const boxTopPad  = 22;
  const boxBotPad  = 8;
  const boxH = boxTopPad + taxHeadH + taxRowH * taxRowCnt + boxBotPad;

  pdfFill(doc, C.light); pdfStroke(doc, C.border);
  doc.setLineWidth(0.5);
  pdfRRect(doc, PAD, y, gstW, boxH, 5, 'FD');
  pdfFill(doc, C.red); pdfRRect(doc, PAD, y, 4, boxH, 2);
  pdfColor(doc, C.dark); doc.setFont('helvetica', 'bold'); doc.setFontSize(8.5);
  pdfTxt(doc, 'Tax Summary', PAD + 12, y + 14);

  const taxRows = f.taxBreakdown.length > 0
    ? f.taxBreakdown.map((t) => [
        t.label,
        `${t.rate.toFixed(1)}%`,
        fmtC(f.taxableSub),
        fmtC(t.amount),
      ])
    : [['No tax record found', '—', '—', '—']];

  drawTable(doc, PAD + 8, y + boxTopPad, [
    { label: 'Component', width: 66,  align: 'left'   },
    { label: 'Rate',      width: 34,  align: 'center' },
    { label: 'Base Amt',  width: 60,  align: 'right'  },
    { label: 'Tax Amt',   width: 52,  align: 'right'  },
  ], [
    ...taxRows,
    ['Total', f.taxBreakdown.length ? `${f.combinedTaxRate.toFixed(1)}%` : '—', '', fmtC(f.totalTax)],
  ], { rowH: taxRowH, headH: taxHeadH, fontSize: 7.5, headFontSize: 7, headBgColor: C.headBg, headColor: C.dark, altRow: false });

  const totRows = [
    { label: 'Non-Taxable Subtotal', value: fmtC(f.nonTaxableSub) },
    { label: 'Taxable Subtotal',     value: fmtC(f.taxableSub)    },
    ...f.taxBreakdown.map((t) => ({
      label: `${t.label} (${t.rate.toFixed(1)}%)`,
      value: fmtC(t.amount),
    })),
  ];
  const rH = 21; let ty = y;
  totRows.forEach((row, i) => {
    pdfFill(doc, i % 2 === 0 ? C.white : C.light);
    pdfRect(doc, totX, ty, totW, rH);
    pdfColor(doc, C.mid); doc.setFont('helvetica', 'normal'); doc.setFontSize(8);
    pdfTxt(doc, row.label, totX + 7, ty + rH / 2 + 3);
    pdfTxt(doc, row.value, totX + totW - 7, ty + rH / 2 + 3, { align: 'right' });
    hline(doc, totX, totX + totW, ty + rH, 0.3);
    ty += rH;
  });
  pdfStroke(doc, C.border); doc.setLineWidth(0.5);
  doc.rect(totX, y, totW, totRows.length * rH, 'S');

  const gtY = ty + 5;
  pdfFill(doc, C.dark); pdfRRect(doc, totX, gtY, totW, 32, 5);
  pdfColor(doc, [155, 170, 188]); doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
  pdfTxt(doc, 'Grand Total', totX + 10, gtY + 20);
  pdfColor(doc, C.white); doc.setFont('helvetica', 'bold'); doc.setFontSize(12);
  pdfTxt(doc, fmtC(f.grandTotal), totX + totW - 10, gtY + 21, { align: 'right' });

  /* ── 5 Special instructions ── */
  y = Math.max(y + boxH, gtY + 42) + 20;

  if (f.isEstimate) {
    pdfFill(doc, C.amberBg); pdfStroke(doc, C.amberBdr);
    doc.setLineWidth(0.5);
    pdfRRect(doc, PAD, y, W - PAD * 2, 28, 5, 'FD');
    pdfColor(doc, [146, 64, 14]); doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5);
    pdfTxt(doc, 'No tax record was found for this order — totals shown are estimated, not the recorded tax breakdown.', PAD + 8, y + 17, { maxWidth: W - PAD * 2 - 16 });
    y += 40;
  }

  if (order.specialInstructions) {
    pdfFill(doc, C.amberBg); pdfStroke(doc, C.amberBdr);
    doc.setLineWidth(0.5);
    pdfRRect(doc, PAD, y, W - PAD * 2, 42, 5, 'FD');
    pdfColor(doc, [146, 64, 14]); doc.setFont('helvetica', 'bold'); doc.setFontSize(7);
    pdfTxt(doc, 'SPECIAL INSTRUCTIONS', PAD + 8, y + 11);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8.5);
    pdfTxt(doc, order.specialInstructions, PAD + 8, y + 26, { maxWidth: W - PAD * 2 - 16 });
    y += 54;
  }

  /* ── 6 Timestamps ── */
  y += 8;
  const tsItems = [
    { label: 'Placed At',      value: formatDate(invoice.placedAt)    },
    { label: 'Completed At',   value: formatDate(invoice.completedAt) },
    { label: 'Payment Method', value: order.paymentMethod             },
    { label: 'GSTIN',          value: restaurant.gstin                },
  ];
  const tsW = (W - PAD * 2) / 4;
  tsItems.forEach((ts, i) => {
    pdfFill(doc, i % 2 === 0 ? C.light : C.white);
    pdfRect(doc, PAD + i * tsW, y, tsW, 32);
    pdfColor(doc, [150, 160, 175]); doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5);
    pdfTxt(doc, ts.label.toUpperCase(), PAD + i * tsW + tsW / 2, y + 10, { align: 'center' });
    pdfColor(doc, C.dark); doc.setFont('helvetica', 'bold'); doc.setFontSize(7.5);
    pdfTxt(doc, ts.value || '—', PAD + i * tsW + tsW / 2, y + 24, { align: 'center' });
  });
  pdfStroke(doc, C.border); doc.setLineWidth(0.5);
  doc.rect(PAD, y, W - PAD * 2, 32, 'S');
  y += 46;

  /* ── 7 Quote footer ── */
  pdfStroke(doc, C.border); doc.setLineWidth(0.6);
  doc.setLineDash([3, 3], 0); doc.line(PAD, y, W - PAD, y); doc.setLineDash([], 0);
  y += 14;

  pdfFill(doc, C.light); pdfRRect(doc, PAD, y, W - PAD * 2, 50, 6);
  pdfColor(doc, C.red); doc.setFont('helvetica', 'bold'); doc.setFontSize(28);
  pdfTxt(doc, '\u201C', PAD + 12, y + 34);
  pdfColor(doc, C.mid); doc.setFont('helvetica', 'italic'); doc.setFontSize(9.5);
  pdfTxt(doc, quote, PAD + 44, y + 18, { maxWidth: W - PAD * 2 - 58 });
  y += 62;

  pdfColor(doc, [170, 180, 195]); doc.setFont('helvetica', 'normal'); doc.setFontSize(7);
  pdfTxt(doc,
    `Invoice ${invoice.invoiceNumber}  ·  Generated ${new Date().toLocaleString('en-IN')}  ·  Computer-generated, no signature required.`,
    W / 2, y, { align: 'center' }
  );

  const safeName = restaurant.name.replace(/[^a-zA-Z0-9 _-]/g, '').replace(/\s+/g, '_');
  doc.save(`Invoice_${safeName}_${invoice.orderNumber}.pdf`);
};

/* ══════════════════════════════════════════════════════════
   REACT COMPONENT
══════════════════════════════════════════════════════════ */
export default function InvoicePage({ orderNumber: propOrderNumber }) {
  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [busy,    setBusy]    = useState(false);
  const printRef = useRef(null);

  const orderNumber = propOrderNumber || window.location.pathname.split('/').pop();

  useEffect(() => {
    const fetch_ = async () => {
      try {
        const res = await fetch(`${host}/invoice/generate/${orderNumber}`);
        if (!res.ok) throw new Error((await res.json()).message || 'Failed');
        setData(await res.json());
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetch_();
  }, [orderNumber]);

  const handleDownload = async () => {
    if (!data || busy) return;
    setBusy(true);
    try { buildPDF(data); }
    finally { setTimeout(() => setBusy(false), 1200); }
  };

  /* ── Loading ── */
  if (loading) return (
    <div className="inv-shell">
      <div className="inv-loader">
        <div className="inv-loader-ring" />
        <p>Building your invoice…</p>
      </div>
    </div>
  );

  /* ── Error ── */
  if (error) return (
    <div className="inv-shell">
      <div className="inv-error">
        <span className="inv-error-icon">⚠</span>
        <p>{error}</p>
      </div>
    </div>
  );

  const { invoice, restaurant, customer, order, items } = data;

  // Tax numbers as recorded for THIS order at completion time —
  // never recomputed from live config. See shapeFinancials().
  const f = shapeFinancials(data.financials);

  return (
    <div className="inv-shell">

      {/* ── Toolbar ── */}
      <div className="inv-toolbar">
        <div className="inv-toolbar-brand">
          <span className="inv-toolbar-dot" />
          {restaurant.name}
        </div>
        <button
          className={`inv-dl-btn ${busy ? 'inv-dl-btn--busy' : ''}`}
          onClick={handleDownload}
          disabled={busy}
        >
          {busy ? (
            <span className="inv-dl-spinner" />
          ) : (
            <svg viewBox="0 0 20 20" fill="none" width="15" height="15">
              <path d="M10 3v9m0 0l-3.5-3.5M10 12l3.5-3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
              <path d="M3 15v1.5A1.5 1.5 0 004.5 18h11a1.5 1.5 0 001.5-1.5V15" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          )}
          {busy ? 'Generating…' : 'Download PDF'}
        </button>
      </div>

      {/* ── Invoice card ── */}
      <div className="inv-paper" ref={printRef}>

        {/* Header */}
        <div className="inv-header">
          <div className="inv-header-left">
            <h1 className="inv-rest-name">{restaurant.name}</h1>
            {restaurant.address && <p className="inv-rest-addr">{restaurant.address}</p>}
            {restaurant.gstin   && <p className="inv-rest-gstin">GSTIN: {restaurant.gstin}</p>}
          </div>
          <div className="inv-header-right">
            <div className="inv-badge">INVOICE</div>
            <p className="inv-inv-number">{invoice.invoiceNumber}</p>
            <p className="inv-inv-date">{formatDate(invoice.placedAt)}</p>
          </div>
        </div>

        {/* Meta pills */}
        <div className="inv-meta-row">
          {[
            { label: 'Customer',    value: customer.name      },
            { label: 'Order Type',  value: order.type         },
            { label: 'Payment',     value: order.paymentMethod },
            { label: 'Status',      value: order.deliveryStatus },
          ].map((p) => (
            <div className="inv-pill" key={p.label}>
              <span className="inv-pill-label">{p.label}</span>
              <span className="inv-pill-value">{p.value || '—'}</span>
            </div>
          ))}
        </div>

        {/* Items table */}
        <div className="inv-section-head">
          <span className="inv-section-bar" />
          <span className="inv-section-title">Order Items</span>
        </div>

        <div className="inv-table-wrap">
          <table className="inv-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Item</th>
                <th>Size</th>
                <th>Taxable</th>
                <th>Qty</th>
                <th>Unit Price</th>
                <th>Disc.</th>
                <th>Amount</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr key={idx}>
                  <td className="inv-td-center">{idx + 1}</td>
                  <td className="inv-td-name">{item.name}</td>
                  <td className="inv-td-center">{item.size}</td>
                  <td className="inv-td-center">
                    <span className={`inv-gst-tag ${item.isTaxable ? 'inv-gst-tag--yes' : 'inv-gst-tag--no'}`}>
                      {item.isTaxable ? 'Yes' : 'No'}
                    </span>
                  </td>
                  <td className="inv-td-center">{item.qty}</td>
                  <td className="inv-td-right">{fmt(item.unitPrice)}</td>
                  <td className="inv-td-center">{item.discount > 0 ? `${item.discount}%` : '—'}</td>
                  <td className="inv-td-right inv-td-amount">{fmt(item.lineNet)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {f.isEstimate && (
          <div className="inv-instructions" style={{ marginTop: 12 }}>
            <span className="inv-instructions-label">Note</span>
            <span>No tax record was found for this order — totals below are estimated, not the recorded tax breakdown.</span>
          </div>
        )}

        {/* Tax + Totals row */}
        <div className="inv-bottom-row">

          {/* Tax summary — one row per tax line as recorded for this order */}
          <div className="inv-gst-box">
            <p className="inv-gst-box-title">Tax Summary</p>
            <table className="inv-gst-table">
              <thead>
                <tr><th>Component</th><th>Rate</th><th>Base Amt</th><th>Tax Amt</th></tr>
              </thead>
              <tbody>
                {f.taxBreakdown.length > 0 ? f.taxBreakdown.map((t) => (
                  <tr key={`${t.group}-${t.label}`}>
                    <td>{t.label}</td>
                    <td>{t.rate.toFixed(1)}%</td>
                    <td>{fmt(f.taxableSub)}</td>
                    <td>{fmt(t.amount)}</td>
                  </tr>
                )) : (
                  <tr><td colSpan={4}>No tax record found</td></tr>
                )}
                <tr className="inv-gst-total-row">
                  <td>Total</td>
                  <td>{f.taxBreakdown.length ? `${f.combinedTaxRate.toFixed(1)}%` : '—'}</td>
                  <td>—</td>
                  <td>{fmt(f.totalTax)}</td>
                </tr>
              </tbody>
            </table>
          </div>

          {/* Totals stack */}
          <div className="inv-totals">
            <div className="inv-total-row">
              <span>Non-Taxable Subtotal</span><span>{fmt(f.nonTaxableSub)}</span>
            </div>
            <div className="inv-total-row">
              <span>Taxable Subtotal</span><span>{fmt(f.taxableSub)}</span>
            </div>
            {f.taxBreakdown.map((t) => (
              <div className="inv-total-row" key={`${t.group}-${t.label}`}>
                <span>{t.label} ({t.rate.toFixed(1)}%)</span><span>{fmt(t.amount)}</span>
              </div>
            ))}
            <div className="inv-grand-total">
              <span>Grand Total</span>
              <span className="inv-grand-total-val">{fmt(f.grandTotal)}</span>
            </div>
          </div>
        </div>

        {/* Special instructions */}
        {order.specialInstructions && (
          <div className="inv-instructions">
            <span className="inv-instructions-label">Special Instructions</span>
            <span>{order.specialInstructions}</span>
          </div>
        )}

        {/* Timestamps */}
        <div className="inv-timestamps">
          {[
            { label: 'Placed At',      value: formatDate(invoice.placedAt)    },
            { label: 'Completed At',   value: formatDate(invoice.completedAt) },
            { label: 'Payment Method', value: order.paymentMethod             },
            { label: 'Order ID',       value: String(invoice.orderId)         },
          ].map((ts) => (
            <div className="inv-ts-item" key={ts.label}>
              <span className="inv-ts-label">{ts.label}</span>
              <span className="inv-ts-value">{ts.value || '—'}</span>
            </div>
          ))}
        </div>

        {/* Quote */}
        <div className="inv-quote">
          <span className="inv-quote-mark">&ldquo;</span>
          <p className="inv-quote-text">{quote}</p>
        </div>

        {/* Footer */}
        <div className="inv-footer">
          <span>{restaurant.name}</span>
          <span>·</span>
          <span>{invoice.invoiceNumber}</span>
          <span>·</span>
          <span>Computer-generated invoice — no signature required</span>
        </div>

      </div>
    </div>
  );
}